import { ReactNode } from 'react';
import { StoreSeller, CartItem, PaymentMethod, PaymentMethodLabel } from '../../models';
import { Button } from '../../components/ui/Button';
import { formatCurrency } from '../../utils/currency';
import { GuestInfo, PaymentEntry } from './types';

function ShieldIcon() {
  return (
    <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 3l7 3v5c0 4.5-3 8.25-7 9.75-4-1.5-7-5.25-7-9.75V6l7-3z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9.5 12l1.75 1.75L14.5 10" />
    </svg>
  );
}

function ExchangeIcon() {
  return (
    <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 7h13l-3-3m3 3l-3 3M20 17H7l3 3m-3-3l3-3" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 24 24">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.122 1.533 5.85L.057 23.535a.75.75 0 0 0 .916.928l5.803-1.524A11.938 11.938 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.853 0-3.587-.5-5.084-1.375l-.361-.214-3.742.981.998-3.648-.235-.374A9.963 9.963 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
    </svg>
  );
}

function SectionCard({ title, accent = false, children }: { title: string; accent?: boolean; children: ReactNode }) {
  return (
    <div
      className={[
        'bg-white rounded-xl p-4 shadow-sm border border-champagne-dark/60 mb-4',
        accent ? 'border-t-4 border-t-chocolate' : '',
      ].join(' ')}
    >
      <h2 className="text-[11px] font-bold uppercase tracking-wider text-chocolate-dark mb-1">{title}</h2>
      <span className="block w-6 h-0.5 bg-chocolate rounded-full mb-3" />
      {children}
    </div>
  );
}

interface ConfirmStepProps {
  seller: StoreSeller;
  paymentEntries: PaymentEntry[];
  total: number;
  observation: string;
  onObservationChange: (v: string) => void;
  items: CartItem[];
  onBack: () => void;
  onSubmit: () => void;
  submitting: boolean;
  guestInfo?: GuestInfo;
}

export function ConfirmStep({
  seller,
  paymentEntries,
  total,
  observation,
  onObservationChange,
  items,
  onBack,
  onSubmit,
  submitting,
  guestInfo,
}: ConfirmStepProps) {
  return (
    <div>
      <SectionCard title="Resumo do pedido" accent>
        <div className="flex flex-col gap-2 mb-3">
          {items.map((item) => (
            <div key={`${item.productId}-${item.variantId}`} className="flex justify-between gap-2 text-sm">
              <span className="text-text-medium min-w-0 break-words">
                {item.productName} ({item.variantLabel}) x{item.quantity}
                {item.isGift && (
                  <svg className="w-3.5 h-3.5 ml-1 inline-block text-chocolate" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18C2.754 7.5 2.25 8.004 2.25 8.625v1.5c0 .621.504 1.125 1.125 1.125z" />
                  </svg>
                )}
              </span>
              <span className="font-medium text-text-dark flex-shrink-0">
                R$ {formatCurrency((item.promotionalPrice ?? item.price) * item.quantity)}
              </span>
            </div>
          ))}
        </div>
        <div className="border-t border-champagne pt-2 flex justify-between font-bold text-text-dark">
          <span>Total</span>
          <span>R$ {formatCurrency(total)}</span>
        </div>
      </SectionCard>

      {guestInfo && (
        <SectionCard title="Seus dados">
          <div className="flex flex-col gap-1 text-sm">
            <div className="flex justify-between">
              <span className="text-text-medium">Nome</span>
              <span className="font-medium text-text-dark">{guestInfo.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-medium">Celular</span>
              <span className="font-medium text-text-dark">{guestInfo.phone}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-medium">Endereço</span>
              <span className="font-medium text-text-dark text-right">
                {guestInfo.street}, {guestInfo.number}
                {guestInfo.complement && ` - ${guestInfo.complement}`}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-medium">Bairro</span>
              <span className="font-medium text-text-dark">{guestInfo.neighborhood}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-medium">Cidade/UF</span>
              <span className="font-medium text-text-dark">
                {guestInfo.city}/{guestInfo.state}
              </span>
            </div>
            {guestInfo.zipCode && (
              <div className="flex justify-between">
                <span className="text-text-medium">CEP</span>
                <span className="font-medium text-text-dark">{guestInfo.zipCode}</span>
              </div>
            )}
          </div>
        </SectionCard>
      )}

      <SectionCard title="Vendedora">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-champagne flex items-center justify-center text-chocolate font-bold">
            {seller.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="text-sm font-medium text-text-dark">{seller.name}</p>
            <p className="text-xs text-text-light">{seller.whatsAppPhone}</p>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Formas de pagamento">
        <div className="flex flex-col gap-1">
          {paymentEntries.map((e, idx) => (
            <div key={idx} className="flex justify-between gap-2 text-sm">
              <span className="text-text-medium min-w-0 break-words">
                {PaymentMethodLabel[e.paymentMethod]}
                {e.paymentMethod === PaymentMethod.CreditCard && e.installments === 2 && ` — 2x de R$ ${formatCurrency(e.amountPaid / 2)}`}
              </span>
              <span className="font-medium text-text-dark flex-shrink-0">
                R$ {formatCurrency(e.amountPaid)}
              </span>
            </div>
          ))}
        </div>
      </SectionCard>

      <div className="mb-4">
        <label className="block text-sm font-medium text-text-dark mb-1">
          Observação (opcional)
        </label>
        <textarea
          value={observation}
          onChange={(e) => onObservationChange(e.target.value)}
          placeholder="Ex: observação do pedido..."
          rows={3}
          className="w-full px-3 py-2 rounded-lg border border-champagne-dark text-text-dark placeholder-text-light bg-white text-sm focus:outline-none focus:ring-2 focus:ring-chocolate/20 focus:border-chocolate resize-none"
        />
      </div>

      <div className="flex items-start gap-2.5 sm:gap-3 bg-champagne/60 rounded-xl p-3 sm:p-3.5 mb-4 border border-champagne-dark/30">
        <div className="w-7 h-7 rounded-full bg-chocolate/10 flex items-center justify-center flex-shrink-0 mt-0.5 text-chocolate">
          <WhatsAppIcon />
        </div>
        <p className="text-xs text-text-medium">
          Ao confirmar, a vendedora entrará em contato para finalizar o pagamento e a entrega.
        </p>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3">
        <span className="flex items-center gap-1 text-[11px] text-text-light">
          <ShieldIcon />
          Pagamento seguro
        </span>
        <span className="flex items-center gap-1 text-[11px] text-text-light">
          <ExchangeIcon />
          Troca fácil em 7 dias
        </span>
        <span className="flex items-center gap-1 text-[11px] text-text-light">
          <WhatsAppIcon />
          Suporte via WhatsApp
        </span>
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={onBack} className="flex-1" disabled={submitting}>
          Voltar
        </Button>
        <Button
          onClick={onSubmit}
          loading={submitting}
          className="flex-1"
        >
          Confirmar pedido
        </Button>
      </div>
    </div>
  );
}
